// material-ui
import {
  Button,
  Dialog,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import GetAppIcon from '@material-ui/icons/GetApp';
import LinkIcon from '@material-ui/icons/Link';
import PublishIcon from '@material-ui/icons/Publish';
import SaveIcon from '@material-ui/icons/Save';
import { debounce } from 'lodash-es';
import { cloneDeep } from 'lodash-es';
import { MTableToolbar } from 'material-table';
import moment from 'moment-timezone';
import React from 'react';

// api
import {
  CampaignActiveActive,
  CampaignActiveInactive,
  IAdserverCampaign,
} from '~/src/dash/api/campaign/campaign';
import { getCampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_methods';
import { getInactiveHeaderBiddersTargetingCampaign } from '~/src/dash/api/campaign/campaign_methods';
import { ICampaignPrebidInfo } from '~/src/dash/api/campaign/campaign_models';
import {
  bulkUpdateBidders,
  deleteCampaign,
  getCampaigns,
  getLastUpdateTimestampByCampaign,
  HeaderBidderActiveActive,
  IBidderWithTableData,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
  updateCampaign,
  uploadBulkuploaderCSVfile,
} from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { transformBidderWithTableDataWrapper } from '~/src/dash/api/headerbidders/utils_headerbidder';
import {
  getAdserverOrder,
  getAdserverOrders,
  IAdserverOrder,
  updateAdserverOrder,
} from '~/src/dash/api/order';
import { GetDebugCampaignsPacingData } from '~/src/dash/api/pacing/controller';
import { IDebugCampaignPacingModel } from '~/src/dash/api/pacing/models';
import { IHttpResponse } from '~/src/dash/api/rest';
// components
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { ICampaignRowData, IListTableColumn, ListTable } from '~/src/dash/components/list-table';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { updateModel } from '~/src/dash/components/model';
import { createChipWihFadingBackgroundVariations } from '~/src/dash/components/model-chip';
import { ModelMediumButtonStyle } from '~/src/dash/components/model-medium-button';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelSnackBarProgressBarContainer } from '~/src/dash/components/model-snackbar-progresbar-container';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import { getBulkUploaderDocumentationWiki } from '~/src/dash/resellers/utils/importResellerConfig';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  genDownloadCampaignsCSVURL,
  getRoutePath,
  navigate,
  RouteCampaignCopy,
  RouteCampaignExisting,
  RouteCampaignNew,
  RouteCampaignSettingsList,
  RouteOrderSettingsNew,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import {
  campaignStatusToChip,
  campaignStatusToString,
  computeCampaignStatus,
  ECampaignStatus,
  ERunningCampaignStatus,
  NOT_RUNNING_CAMPAIGN_STATUSES,
  RUNNING_CAMPAIGN_STATUSES,
} from '~/src/dash/utils/campaign-status';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateFromUTCToOffset, hasCampaignEnded, parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateCompanyName } from '~/src/dash/utils/obfuscate';
import {
  allowedEmptyIPermissionCheck,
  IPermissionCheck,
  IS_AUTHORIZED,
} from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
// utils
import { routeName } from '~/src/dash/utils/routes';
import UploadManager from '~/src/dash/utils/upload_manager';
import { REACTIVATE_BIDDERS } from '~/src/dash/views/campaign/common';
// views
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  campaigns?: IAdserverCampaign[];
  orderID: number;
  // order active lets the list know to refresh if the active status of the order has changed
  orderActive?: boolean;
  getOrder?: () => void; // tells the parent order view to get the order
  hideTopbar?: boolean; // hide topbar. TODO: need to separate the item list out as a reusable component so that we can use it in other views
}

interface IState extends IBaseState {
  campaigns: IAdserverCampaign[];
  count: number;
  // indicate api call being completed
  updating: boolean;
  // indicates when render is allowed
  loading: boolean;
  // snackbar
  snackbarMessage: string;
  snackbarError: boolean;
  // permission
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  // used for top left dropdown
  runningCampaignFilter: ERunningCampaignStatus;
  prebidInfoFilter: 'show_all' | 'low_pacing' | 'site_weight';
  lastUpdate: moment.Moment;
  // orders
  AdserverOrders: IAdserverOrder[];
  // start and end date filters
  startDateFilterStart?: moment.Moment;
  startDateFilterEnd?: moment.Moment;
  endDateFilterStart?: moment.Moment;
  endDateFilterEnd?: moment.Moment;

  campaignPrebidInfo: ICampaignPrebidInfo[];
  campaignIDToPacingData: { [key: string]: IDebugCampaignPacingModel };
  // advertiser names for filtering
  advertiserIDAndNames: string[];
  advertiserFilter: string;
  openReactivateBiddersModal: boolean;
  ignoreReactivateBidders: boolean;
  inactiveBiddersTargetingCampaign: IBidderWithTableData[];
  campaignUpdating: IAdserverCampaign;
}

export const FilterActiveChip = createChipWihFadingBackgroundVariations(
  '#00C488',
  'rgba(0,196,136,0.10)',
  'rgba(0,196,136,0.01)',
  'rgba(0,196,136,0.15)',
);

export const LiveDataChip = createChipWihFadingBackgroundVariations(
  '#BC83FF',
  'rgba(244,235,255,0.5)',
  'rgba(244,235,255,0.20)',
  'rgba(244,235,255,0.50)',
);

export const CampaignSettingsListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      button: ModelMediumButtonStyle,
      menuButtonText: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '$434343',
      },
      menuButtonTextBlue: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#4952FF',
      },
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      textField: {
        margin: 0,
        minWidth: '400px',
        width: '30%',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      campaigns: [] as IAdserverCampaign[],
      campaignStatus: [] as ECampaignStatus[],
      count: 0,
      updating: false,
      loading: false,
      snackbarMessage: '',
      snackbarError: false,
      readAuthorization: allowedEmptyIPermissionCheck,
      writeAuthorization: allowedEmptyIPermissionCheck,
      runningCampaignFilter: ERunningCampaignStatus.Running,
      prebidInfoFilter: 'show_all' as 'show_all' | 'low_pacing' | 'site_weight',
      lastUpdate: moment(),
      AdserverOrders: [] as IAdserverOrder[],
      startDateFilterStart: undefined,
      startDateFilterEnd: undefined,
      endDateFilterStart: undefined,
      endDateFilterEnd: undefined,
      campaignPrebidInfo: [] as ICampaignPrebidInfo[],
      campaignIDToPacingData: {} as { [key: string]: IDebugCampaignPacingModel },
      advertiserIDAndNames: [] as string[],
      advertiserFilter: 'Show All',
      openReactivateBiddersModal: false,
      ignoreReactivateBidders: true,
      inactiveBiddersTargetingCampaign: [] as IBidderWithTableData[],
      campaignUpdating: {} as IAdserverCampaign,
    };

    uploadManager = new UploadManager();

    _updateCampaign = debounce(
      (count: number, campaign: IAdserverCampaign, campaignSwitchedToActive: boolean) => {
        if (count !== this.state.count) {
          return;
        }
        // if the campaign switched to active, we need to check if the parent order is inactive,
        // if it is, it must be switched to active (but not the other child line items)
        if (campaignSwitchedToActive) {
          // get the parent order
          getAdserverOrder(
            campaign.AdserverOrderID.toString(),
            this.props.session.publisher.ID,
          ).then((order: IAdserverOrder) => {
            // if the parent order is inactive && campaignSwitchedToActive, we need to update the order to active
            if (order.Active === ResourceActiveDisabled) {
              const updatedOrder = {
                ID: order.ID,
                Active: ResourceActiveEnabled,
              } as IAdserverOrder;
              // since we are only updating the active field, this is a partial update rather than a full update
              // from this, we do not want to update the children line items to be the same as the order
              updateAdserverOrder(updatedOrder, this.props.session.publisher.ID, false, false).then(
                () => {
                  if (this.props.getOrder) {
                    this.props.getOrder();
                  }
                },
              );
            }

            // need to prompt reactivating bidders targeting this campaign
            if (!this.state.ignoreReactivateBidders) {
              getInactiveHeaderBiddersTargetingCampaign(
                campaign.ID,
                this.props.session.publisher.ID,
              )
                .then((val: IHttpResponse<IBidderWithTableData[]>) => {
                  const headerbidders = [] as IBidderWithTableData[];
                  if (val.parsedBody) {
                    for (const item of val.parsedBody as IBidderWithTableData[]) {
                      const bidder = transformBidderWithTableDataWrapper(item);
                      headerbidders.push(bidder);
                    }
                  }
                  if (headerbidders.length > 0) {
                    this.setState({
                      inactiveBiddersTargetingCampaign: headerbidders,
                      openReactivateBiddersModal: true,
                    });
                  } else {
                    this.setState(
                      {
                        ignoreReactivateBidders: true,
                        updating: false,
                      },
                      () => this.updateCampaign(this.state.campaignUpdating, false),
                    );
                  }
                })
                .catch(() => {
                  this.setState({
                    snackbarMessage: 'error fetching bidders targeting this campaign',
                  });
                });
              return;
            }

            // update the campaign and compute campaign statuses after update
            updateCampaign({
              body: {
                publisher_id: this.props.session.publisher.ID,
                campaign_id: campaign.ID,
                campaign,
                updateActive: true,
              },
            }).then(
              (campaign: IAdserverCampaign) => {
                this.setState(
                  {
                    campaigns: updateModel(this.state.campaigns, campaign),
                    snackbarMessage: `Successfully updated line item [${campaign.ID}] ${campaign.Name}`,
                    updating: false,
                  },
                  () => this.computeCampaignsStatus(this.state.campaigns, false),
                );
              },
              () => {
                this.setState({
                  updating: false,
                  snackbarMessage: `Failed to update line item [${campaign.ID}] ${campaign.Name}`,
                  snackbarError: true,
                });
              },
            );
          });
        } else {
          // update the campaign and compute campaign statuses after update
          updateCampaign({
            body: {
              publisher_id: this.props.session.publisher.ID,
              campaign_id: campaign.ID,
              campaign,
              updateActive: true,
            },
          }).then(
            (campaign: IAdserverCampaign) => {
              this.setState(
                {
                  campaigns: updateModel(this.state.campaigns, campaign),
                  snackbarMessage: `Successfully updated line item [${campaign.ID}] ${campaign.Name}`,
                  updating: false,
                },
                () => this.computeCampaignsStatus(this.state.campaigns, false),
              );
            },
            () => {
              this.setState({
                updating: false,
                snackbarMessage: `Failed to update line item [${campaign.ID}] ${campaign.Name}`,
                snackbarError: true,
              });
            },
          );
        }
      },
      500,
    );

    componentDidMount() {
      // Rights
      this.setState({
        readAuthorization: IS_AUTHORIZED(['read_campaign'], this.props.user.user),
        writeAuthorization: IS_AUTHORIZED(['write_campaign'], this.props.user.user),
      });

      // get timestamp
      getLastUpdateTimestampByCampaign({
        query: {
          publisher_id: this.props.session.publisher.ID,
        },
      }).then(
        (data: { LastUpdateTimestampByCampaign: number }) => {
          this.setState({
            lastUpdate: moment.unix(data.LastUpdateTimestampByCampaign),
          });
        },
        () => {
          console.error('failed to get last update timestamp by auctions');
        },
      );

      // get orders (for advertisers)
      this.getOrders();

      // get campaigns from api
      if (this.props.campaigns === undefined) {
        this.refreshModels();
      } else {
        this.computeCampaignsStatus(this.props.campaigns, false);
      }
    }

    componentDidUpdate(prevProps: IProps) {
      // if there are campaigns and there has been a change in the order or activity, reset state and recalc status
      if (
        (prevProps.orderID !== this.props.orderID ||
          prevProps.orderActive !== this.props.orderActive) &&
        this.props.campaigns &&
        this.props.campaigns.length
      ) {
        this.setState(
          {
            campaigns: this.props.campaigns || ([] as IAdserverCampaign[]),
          },
          () =>
            this.computeCampaignsStatus(this.state.campaigns || ([] as IAdserverCampaign[]), false),
        );
      }
    }

    getOrders = () => {
      getAdserverOrders(this.props.session.publisher.ID)
        .then((AdserverOrders: IAdserverOrder[]) => {
          // create map of advertisers for filter
          const advertiserIDAndNamesMap = {} as { [key: string]: boolean };
          advertiserIDAndNamesMap['Show All'] = true;
          for (let idx = 0; idx < AdserverOrders.length; idx++) {
            // if orderID is defined, this list only shows line items under this order
            if (this.props.orderID !== undefined && AdserverOrders[idx].ID !== this.props.orderID) {
              continue;
            }
            const advertiser = AdserverOrders[idx].Advertiser;
            const advertiserID = AdserverOrders[idx].AdvertiserID;
            if (advertiser) {
              advertiserIDAndNamesMap[`[${advertiserID}] ${advertiser.Name}`] = true;
            }
          }
          this.setState({
            AdserverOrders,
            advertiserIDAndNames: Object.keys(advertiserIDAndNamesMap),
          });
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading parent orders.',
          });
        });
    };

    updateCampaign = (campaign: IAdserverCampaign, campaignSwitchedToActive: boolean) => {
      if (this.state.updating) {
        return;
      }
      this.setState(
        {
          updating: true,
          count: this.state.count + 1,
        },
        () => {
          this._updateCampaign(this.state.count, campaign, campaignSwitchedToActive);
        },
      );
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    handleChangeActive = (campaignID: string, checked: boolean) => {
      if (this.state.updating) {
        return;
      }
      const campaign = this.state.campaigns.find((c: IAdserverCampaign) => campaignID === c.ID);
      if (campaign) {
        campaign.Active = checked ? CampaignActiveActive : CampaignActiveInactive;

        if (
          campaign.Active === CampaignActiveActive &&
          moment(campaign.EndDate).isAfter(moment(new Date()).tz(campaign.Timezone))
        ) {
          this.setState({
            campaignUpdating: campaign,
            ignoreReactivateBidders: false,
          });
        }
        // if the camppaign was switched to active, we should pass that to update campaign
        // which needs to check if the parent order is inactive, since the order will now also become active
        this.updateCampaign(campaign, campaign.Active === CampaignActiveActive);
      }
    };

    computeCampaignsStatus = (campaigns: IAdserverCampaign[], onRefresh: boolean) => {
      // Update campaigns to include status
      campaigns.forEach((campaign: IAdserverCampaign) => {
        campaign.Status = computeCampaignStatus(
          campaign,
          this.props.session.publisher.Timezone,
          parseInt(campaign.CRONQuantityCount, 10) || 0,
        );
      });
      this.setState({
        campaigns,
        loading: false,
      });

      // also refresh the pacing debug models
      this.getCampaignsPrebidInfo(campaigns);
      if (!onRefresh) {
        this.fetchCampaignsPacingData(campaigns);
      }
    };

    _refreshModels = (count: number) => {
      if (count !== this.state.count) {
        return;
      }

      if (!this.state.readAuthorization.isAuthorized) {
        return;
      }

      getCampaigns({
        query: {
          publisher_id: this.props.session.publisher.ID,
        },
      }).then((campaigns: IAdserverCampaign[]) => {
        if (this.props.orderID) {
          campaigns = campaigns.filter(
            (campaign: IAdserverCampaign) => campaign.AdserverOrderID === this.props.orderID,
          );
        }
        this.computeCampaignsStatus(campaigns, true);
        this.getCampaignsPrebidInfo(campaigns);
        this.fetchCampaignsPacingData(campaigns);
      });
    };

    refreshModels = () => {
      const count = this.state.count + 1;
      this.setState(
        {
          loading: true,
          count,
        },
        () => {
          this._refreshModels(count);
        },
      );
    };

    fetchCampaignsPacingData = (campaigns: IAdserverCampaign[]) => {
      const campaignIDToPacingData = {} as { [key: string]: IDebugCampaignPacingModel };
      const campaignIDs = campaigns
        .filter((c) => {
          const isLowPacingEnabled = c.DisableLowPacing === 1;
          const isCampaignActive = c.Active === CampaignActiveActive;
          const isCampaignDelivering = c.Status === ECampaignStatus.Delivering;
          return isLowPacingEnabled && isCampaignActive && isCampaignDelivering;
        })
        .map((c) => c.ID);

      GetDebugCampaignsPacingData(this.props.session.publisher.ID, campaignIDs.join(',')).then(
        (resp) => {
          const debugData = resp[0];
          if (debugData !== undefined) {
            for (const debugDatum of debugData) {
              campaignIDToPacingData[debugDatum.CampaignID] = debugDatum;
            }
          }
          this.setState({
            campaignIDToPacingData,
          });
        },
      );
    };

    getCampaignsPrebidInfo = (campaigns: IAdserverCampaign[]) => {
      const campaignsIDS = campaigns.map((c: IAdserverCampaign): string => c.ID).join(',');

      getCampaignPrebidInfo(campaignsIDS, this.props.session.publisher.ID).then(
        ([campaignPrebidInfo, err]) => {
          if (
            err !== undefined ||
            campaignPrebidInfo === undefined ||
            campaignPrebidInfo === null
          ) {
            console.error(err);
            this.setState({
              campaignPrebidInfo: [],
            });
          } else {
            this.setState({
              campaignPrebidInfo,
            });
          }
        },
      );
    };

    getCampaignSettingsRoute = (id: string): string => {
      const campaign = this.state.campaigns.find((c: IAdserverCampaign) => c.ID === id);
      if (campaign) {
        return getRoutePath(RouteCampaignExisting, extractRouteParams(this.props), {
          c: id,
          order: campaign.AdserverOrderID.toString(),
        });
      } else {
        return getRoutePath(RouteCampaignSettingsList, extractRouteParams(this.props));
      }
    };

    cloneCampaignSettings = (cloneID: string) => {
      const campaign = this.state.campaigns.find((c: IAdserverCampaign) => c.ID === cloneID);
      if (campaign) {
        navigate(RouteCampaignCopy, extractRouteParams(this.props), {
          c: cloneID,
          order: campaign.AdserverOrderID.toString(),
        });
      } else {
        navigate(RouteCampaignSettingsList, extractRouteParams(this.props));
      }
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    // deletes the campaign and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    handleDeleteCampaign = (campaign_id: string) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteCampaign({
            query: {
              publisher_id: this.props.session.publisher.ID,
              campaign_id,
            },
          })
            .then(() => {
              const campaigns = this.state.campaigns.filter(
                (campaign: IAdserverCampaign) => campaign.ID.toString() !== campaign_id,
              );
              this.setState({
                campaigns,
                snackbarMessage: 'Successfully deleted line item',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting line item',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    renderStatusChip = (row: ICampaignRowData) => {
      const campaignStatus =
        row.Campaign.Status === undefined ? ECampaignStatus.Unknown : row.Campaign.Status;
      const chip = campaignStatusToChip(campaignStatus);

      if (chip === undefined) {
        return campaignStatusToString(campaignStatus);
      }

      const ChipComponent = chip.component;

      return (
        <Grid container direction='row' justify='center' alignItems='center'>
          {row.TooltipEnabled && (
            <Grid item style={{ paddingRight: '2px' }}>
              <Tooltip title={this.generateCampaignPacingTooltipText()} placement='bottom'>
                <img src='/assets/shared/svg/Reco.svg' style={{ width: '20px' }} />
              </Tooltip>
            </Grid>
          )}
          <Grid item>
            <Tooltip title={chip.tooltip} placement='bottom'>
              <ChipComponent
                onClick={() => this.getCampaignSettingsRoute(row.Campaign.ID)}
                label={campaignStatusToString(campaignStatus)}
              />
            </Tooltip>
          </Grid>
        </Grid>
      );
    };

    // create a campaign if we are on the Order => Line Items list
    createCampaign = () => {
      if (this.state.updating) {
        return;
      }
      navigate(RouteCampaignNew, extractRouteParams(this.props), {
        order: this.props.orderID.toString(),
      });
    };

    // create an order if we are on the All Line Items List
    createOrder = () => {
      if (this.state.updating) {
        return;
      }
      navigate(RouteOrderSettingsNew, extractRouteParams(this.props));
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    compareStartDates = (rowData1: ICampaignRowData, rowData2: ICampaignRowData) => {
      return rowData1.StartDate.isBefore(rowData2.StartDate) ? -1 : 1;
    };

    compareEndDates = (rowData1: ICampaignRowData, rowData2: ICampaignRowData) => {
      return rowData1.EndDate.isBefore(rowData2.EndDate) ? -1 : 1;
    };

    calculatePacing = (campaign: IAdserverCampaign) => {
      const dateStart = moment.tz(campaign.StartDate, this.props.session.publisher.Timezone);
      const dateEnd = moment.tz(campaign.EndDate, this.props.session.publisher.Timezone);
      const impressions = parseInt(campaign.CRONQuantityCount, 10) || 0;
      let pacing = impressions / parseInt(campaign.Quantity, 10);
      if (!hasCampaignEnded(dateEnd)) {
        const durationTotal = moment.duration(dateEnd.diff(dateStart)).asHours();
        const duration = moment.duration(this.state.lastUpdate.diff(dateStart)).asHours();
        const expectation = Math.round(
          (parseInt(campaign.Quantity, 10) * duration) / durationTotal,
        );
        pacing = impressions / expectation;
      }

      if (isNaN(pacing) || pacing === Infinity) {
        pacing = 0;
      }
      return parseFloat((pacing * 100).toFixed(2));
    };

    shouldEnableCampaignPacingTooltip = (campaign: IAdserverCampaign): boolean => {
      const campaignInfo = this.state.campaignPrebidInfo.find(
        (c: ICampaignPrebidInfo) => c.campaign_id === +campaign.ID,
      );

      if (campaignInfo === undefined) {
        return false;
      }

      return campaignInfo.low_pacing || campaignInfo.site_weight_enabled;
    };

    generateCampaignPacingTooltipText = (): string => {
      return 'To prevent over-delivery, raw requests are being sampled to determine the rate of traffic. This happens when either channel weights are used, or overall scheduled impressions is beneath 150 per hour.';
    };
    // filter menu helper functions

    handleStartDateFilterChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState({
        startDateFilterStart: start,
        startDateFilterEnd: end,
      });
    };

    handleEndDateFilterChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState({
        endDateFilterStart: start,
        endDateFilterEnd: end,
      });
    };

    handleClearFilters = () => {
      this.setState({
        startDateFilterStart: undefined,
        startDateFilterEnd: undefined,
        endDateFilterStart: undefined,
        endDateFilterEnd: undefined,
        runningCampaignFilter: ERunningCampaignStatus.Running,
        prebidInfoFilter: 'show_all',
        advertiserFilter: 'Show All',
      });
    };

    // filter menu render functions

    renderMenuTitle = () => {
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid container direction='row' alignItems='center' spacing={2}>
          <Grid item>
            <Typography
              style={{
                marginLeft: 10,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: getColorTheme('secondaryButtonText'),
              }}
            >
              FILTERS
            </Typography>
          </Grid>
          <Grid item>
            <FilterListIcon
              style={{
                fontSize: '1rem',
                fontWeight: 500,
                color: getColorTheme('secondaryButtonText'),
              }}
            />
          </Grid>
          {activeFilterCount !== 0 && (
            <Grid item xs={2} onClick={this.handleClearFilters}>
              <FilterActiveChip label={`${activeFilterCount} ACTIVE FILTERS`} />
            </Grid>
          )}
        </Grid>
      );
    };

    renderStatusRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Status</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Select
                name='runningCampaignFilter'
                value={this.state.runningCampaignFilter}
                onChange={this.handleChange}
                style={{ fontSize: 14 }}
              >
                <MenuItem value={ERunningCampaignStatus.Running}>Running</MenuItem>
                <MenuItem value={ERunningCampaignStatus.NotRunning}>Not Running</MenuItem>
                <MenuItem value={'Show All'}>Show All</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderAdvertiserRow = () => {
      const { advertiserIDAndNames } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Advertisers</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Select
                name='advertiserFilter'
                value={this.state.advertiserFilter}
                onChange={this.handleChange}
                style={{ fontSize: 14 }}
              >
                {advertiserIDAndNames.map((name: string) => (
                  <MenuItem value={name}>{name}</MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDebugFilterRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>
                Debug (Prebid Data) *
              </Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <Select
                name='prebidInfoFilter' // low pacing / site weight enabled
                value={this.state.prebidInfoFilter}
                onChange={this.handleChange}
                style={{ fontSize: '14px' }}
              >
                <MenuItem value={'show_all'}>Show All</MenuItem>
                <MenuItem value={'low_pacing'}>Low Pacing</MenuItem>
                <MenuItem value={'site_weight'}>Site Weight Enabled</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderStartDateRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>Start Date Range</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <DateTimeRangeComponent
                timezone={'UTC'}
                disableTime={true}
                start={this.state.startDateFilterStart}
                end={this.state.startDateFilterEnd}
                onChange={this.handleStartDateFilterChange}
                enableSelectAll={true}
                openDirection={'up'}
                smallCalendar={true}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderEndDateRow = () => {
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2}>
              <Typography style={{ fontSize: 14, paddingLeft: 10 }}>End Date Range</Typography>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={9}>
              <DateTimeRangeComponent
                timezone={'UTC'}
                disableTime={true}
                start={this.state.endDateFilterStart}
                end={this.state.endDateFilterEnd}
                onChange={this.handleEndDateFilterChange}
                enableSelectAll={true}
                openDirection={'up'}
                smallCalendar={true}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderClearFilterRow = () => {
      // determine how many filters are active
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={2} onClick={this.handleClearFilters}>
              <Typography
                style={{
                  fontSize: 14,
                  paddingLeft: 10,
                  fontWeight: 500,
                  color: getColorTheme('secondaryButtonText'),
                }}
              >
                CLEAR FILTERS
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderMenuButton = () => {
      // menu dropdown
      const menuRow = {
        closeOnClick: true,
        render: this.renderMenuTitle,
      };
      // status dropdown
      const statusRow = {
        closeOnClick: false,
        render: this.renderStatusRow,
      };
      // advertiser dropdown
      const advertiserRow = {
        closeOnClick: false,
        render: this.renderAdvertiserRow,
      };
      // debug dropdown
      const debugFilterRow = {
        closeOnClick: false,
        render: this.renderDebugFilterRow,
      };
      // start date dropdown
      const startDateRow = {
        closeOnClick: false,
        render: this.renderStartDateRow,
      };
      // end date dropdown
      const endDateRow = {
        closeOnClick: false,
        render: this.renderEndDateRow,
      };
      // clear filters
      const clearFilterRow = {
        closeOnClick: false,
        render: this.renderClearFilterRow,
      };
      let menuButtonItems = [
        menuRow,
        statusRow,
        advertiserRow,
        startDateRow,
        endDateRow,
        clearFilterRow,
      ];
      if (hasRoleSuperUser(this.props.user.user)) {
        menuButtonItems = [
          menuRow,
          statusRow,
          advertiserRow,
          debugFilterRow,
          startDateRow,
          endDateRow,
          clearFilterRow,
        ];
      }
      return (
        <MenuButton
          titleRender={this.renderMenuTitle}
          items={menuButtonItems}
          minWidth={600}
          enableGreyFirstItem={true}
        />
      );
    };

    countActiveFilters = (): number => {
      let activeFilterCount = 0;
      if (
        this.state.runningCampaignFilter === ERunningCampaignStatus.Running ||
        this.state.runningCampaignFilter === ERunningCampaignStatus.NotRunning
      ) {
        activeFilterCount++;
      }
      if (this.state.startDateFilterStart || this.state.startDateFilterEnd) {
        activeFilterCount++;
      }
      if (this.state.endDateFilterStart || this.state.endDateFilterEnd) {
        activeFilterCount++;
      }
      if (this.state.advertiserFilter !== 'Show All') {
        activeFilterCount++;
      }
      if (this.state.prebidInfoFilter !== 'show_all') {
        activeFilterCount++;
      }

      return activeFilterCount;
    };

    // filters campaigns from filter dropdown, when filter is added, also update countActiveFilters
    filterCampaigns = (campaigns: IAdserverCampaign[]): IAdserverCampaign[] => {
      return campaigns.filter((campaign: IAdserverCampaign) => {
        // running status filter
        if (
          this.state.runningCampaignFilter === ERunningCampaignStatus.Running &&
          !RUNNING_CAMPAIGN_STATUSES.includes(campaign.Status || ECampaignStatus.Unknown)
        ) {
          return false;
          // Completed and inactive campaigns should appear as Not Running
        } else if (
          this.state.runningCampaignFilter === ERunningCampaignStatus.NotRunning &&
          !NOT_RUNNING_CAMPAIGN_STATUSES.includes(campaign.Status || ECampaignStatus.Unknown)
        ) {
          return false;
        }

        // start date start filter, remove all line items with start date before filter
        if (this.state.startDateFilterStart) {
          if (
            formatDateFromUTCToOffset(campaign.StartDate, 'UTC').isBefore(
              this.state.startDateFilterStart,
            )
          ) {
            return false;
          }
        }
        // start date end filter, remove all line items with start date after filter
        if (this.state.startDateFilterEnd) {
          if (
            formatDateFromUTCToOffset(campaign.StartDate, 'UTC').isAfter(
              this.state.startDateFilterEnd,
            )
          ) {
            return false;
          }
        }
        // end date start filter, remove all line items with end date before filter
        if (this.state.endDateFilterStart) {
          if (
            formatDateFromUTCToOffset(campaign.EndDate, 'UTC').isBefore(
              this.state.endDateFilterStart,
            )
          ) {
            return false;
          }
        }
        // end date start filter, remove all line items with end date after filter
        if (this.state.endDateFilterEnd) {
          if (
            formatDateFromUTCToOffset(campaign.EndDate, 'UTC').isAfter(this.state.endDateFilterEnd)
          ) {
            return false;
          }
        }

        // advertiser name filter
        if (this.state.advertiserFilter !== 'Show All') {
          const order = this.state.AdserverOrders.find(
            (order: IAdserverOrder) => order.ID === campaign.AdserverOrderID,
          );
          let advertiserWithID = 'Unknown';
          if (order && order.Advertiser) {
            advertiserWithID = order.Advertiser
              ? `[${order.AdvertiserID}] ${obfuscateCompanyName(
                  order.Advertiser,
                  this.props.user.user.Username,
                  order.Advertiser.Name,
                )}`
              : '';
          }
          // if there is no advertiser but there's a filter or if the name does  not match
          if (this.state.advertiserFilter !== advertiserWithID) {
            return false;
          }
        }

        // debug filter
        const campaignInfo = this.state.campaignPrebidInfo.find(
          (c: ICampaignPrebidInfo) => c.campaign_id === +campaign.ID,
        );
        // if looking for low pacing, remove all line items that are not low pacing
        if (
          this.state.prebidInfoFilter === 'low_pacing' &&
          !(campaignInfo || { low_pacing: false }).low_pacing
        ) {
          return false;
        }
        // if looking for site weight enabled, remove all line items that do not have site weight enabled
        if (
          this.state.prebidInfoFilter === 'site_weight' &&
          !(campaignInfo || { site_weight_enabled: false }).site_weight_enabled
        ) {
          return false;
        }
        return true;
      });
    };

    uploadCSV = (isNewCSV: boolean, { target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }
        target.value = '';
        const uploadKey = this.uploadManager.addUpload(file.name);

        uploadBulkuploaderCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          order_id: (this.props.orderID || '').toString(),
          template_id: isNewCSV ? 'campaign_new' : 'campaign_updates',
          file,
          managerFunc: (xhr: XMLHttpRequest) => {
            this.uploadManager.addListenersToUpload(uploadKey, xhr);
          },
        })
          .then((json: any) => {
            if (json.response.success) {
              try {
                this.uploadManager.signalSuccess(uploadKey);
              } catch (e) {
                this.uploadManager.signalFailure(uploadKey);
              }
            } else {
              this.uploadManager.signalFailure(uploadKey);
            }
            if (json.response.errors && json.response.errors.length > 0) {
              this.setState({
                snackbarMessage: json.response.errors,
                snackbarError: true,
              });
            } else {
              this.refreshModels();
              this.setState({
                snackbarError: false,
                snackbarMessage: 'Line items successfully updated',
              });
            }
          })
          .catch(() => {
            this.uploadManager.signalFailure(uploadKey);
            this.setState({
              snackbarMessage: 'Line items failed to update',
            });
          });
      }
    };

    renderBulkUploadTitle = () => {
      const { classes } = this.props;
      return (
        <Grid item>
          <Button className={classes.button} style={{ width: 150 }}>
            <Grid container direction='row' alignItems='center' spacing={1}>
              <Grid item>
                <PublishIcon
                  style={{
                    marginTop: 5,
                    color: getColorTheme('primaryButtons'), // Color for upload button of Campaign Settings
                  }}
                />
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonText}>BULK UPLOAD</Typography>
              </Grid>
            </Grid>
          </Button>
        </Grid>
      );
    };

    renderDownloadNewCSV = () => {
      return (
        <Grid item>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item>
              <GetAppIcon
                style={{
                  color: '#4952FF',
                }}
              />
            </Grid>
            <Grid item>
              <a
                target='_blank'
                rel='noopener'
                style={{ textDecoration: 'none' }}
                download
                href={genDownloadCampaignsCSVURL(
                  this.props.session.publisher.ID,
                  true,
                  (this.props.orderID || '').toString(),
                  this.props.session.publisher.Name,
                )}
              >
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  CREATE NEW LINES
                </Typography>
              </a>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDownloadUpdateCSV = () => {
      const { orderID } = this.props;
      return (
        <Grid item>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item>
              <GetAppIcon
                style={{
                  color: '#4952FF',
                }}
              />
            </Grid>
            <Grid item>
              <a
                target='_blank'
                rel='noopener'
                style={{ textDecoration: 'none' }}
                download
                href={genDownloadCampaignsCSVURL(
                  this.props.session.publisher.ID,
                  false,
                  (orderID || '').toString(),
                  this.props.session.publisher.Name,
                )}
              >
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  CREATE LINE UPDATES
                </Typography>
              </a>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderUploadCreateCSV = () => {
      return (
        <label>
          <Grid item>
            <Grid
              container
              direction='row'
              alignItems='center'
              spacing={2}
              style={{ cursor: 'pointer' }}
            >
              <Grid item>
                <PublishIcon
                  style={{
                    color: '#4952FF',
                  }}
                ></PublishIcon>
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  UPLOAD NEW LINES
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <input
            type='file'
            style={{ display: 'none' }}
            accept='.csv'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              this.uploadCSV(true, event);
            }}
          />
        </label>
      );
    };

    renderUploadUpdateCSV = () => {
      return (
        <label>
          <Grid item>
            <Grid
              container
              direction='row'
              alignItems='center'
              spacing={2}
              style={{ cursor: 'pointer' }}
            >
              <Grid item>
                <PublishIcon
                  style={{
                    color: '#4952FF',
                  }}
                ></PublishIcon>
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  UPLOAD LINE UPDATES
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <input
            type='file'
            style={{ display: 'none' }}
            accept='.csv'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              this.uploadCSV(false, event);
            }}
          />
        </label>
      );
    };

    renderWikiLink = () => {
      return (
        <label>
          <Grid item>
            <a
              href={getBulkUploaderDocumentationWiki()}
              target='_blank'
              style={{ textDecoration: ' none' }}
            >
              <Grid
                container
                direction='row'
                alignItems='center'
                spacing={2}
                style={{ cursor: 'pointer' }}
              >
                <Grid item>
                  <LinkIcon
                    style={{
                      color: '#4952FF',
                    }}
                  ></LinkIcon>
                </Grid>
                <Grid item>
                  <Typography className={this.props.classes.menuButtonTextBlue}>
                    BULK UPLOADER WIKI
                  </Typography>
                </Grid>
              </Grid>
            </a>
          </Grid>
        </label>
      );
    };

    // renders the dropdown button for bulk upload options
    renderBulkUploadButton = () => {
      const downloadNewCSVRow = {
        closeOnClick: true,
        render: this.renderDownloadNewCSV,
      };
      const downloadUpdateCSVRow = {
        closeOnClick: true,
        render: this.renderDownloadUpdateCSV,
      };
      const uploadCreateCSVRow = {
        closeOnClick: true,
        render: this.renderUploadCreateCSV,
      };
      const uploadUpdateCSVRow = {
        closeOnClick: true,
        render: this.renderUploadUpdateCSV,
      };
      const wikiLinkRow = {
        closeOnClick: true,
        render: this.renderWikiLink,
      };
      const menuButtonItems = [
        downloadNewCSVRow,
        downloadUpdateCSVRow,
        uploadCreateCSVRow,
        uploadUpdateCSVRow,
        wikiLinkRow,
      ];
      return (
        <MenuButton
          titleRender={this.renderBulkUploadTitle}
          items={menuButtonItems}
          minWidth={250}
          marginTop={60}
          marginLeft={this.props.orderID === undefined ? -30 : -95}
          enableDividers={true}
        />
      );
    };

    // renders the custom tool bar for the main list
    renderCustomToolbar = (props: any) => {
      const { classes, orderID } = this.props;

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid
                container
                direction='column'
                justify='flex-start'
                alignItems='flex-start'
                spacing={0}
              >
                <Grid item>
                  {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
                  <Grid item className='MTableToolbarWrapper'>
                    <MTableToolbar {...copyProps} />
                  </Grid>
                </Grid>
                <Grid item>{this.renderMenuButton()}</Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems='center'>
                <Grid item>{this.renderBulkUploadButton()}</Grid>
                {orderID !== undefined &&
                  // if on new order without saving
                  (orderID === 0 ? (
                    <Tooltip title='Must save order before adding line items'>
                      <Grid item>
                        <RowButton
                          right={
                            <ModelMediumButton
                              variant='contained'
                              color='primary'
                              style={{ paddingLeft: 15, paddingRight: 15 }}
                            >
                              + Add Line Item
                            </ModelMediumButton>
                          }
                        />
                      </Grid>
                    </Tooltip>
                  ) : (
                    // if on existing order
                    <Grid item>
                      <RowButton
                        right={
                          <ModelMediumButton
                            variant='contained'
                            color='primary'
                            style={{ paddingLeft: 15, paddingRight: 15 }}
                            onClick={this.createCampaign}
                          >
                            + Add Line Item
                          </ModelMediumButton>
                        }
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderReactivateBiddersDialog = () => {
      return (
        // weird margins to make the search box align
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='row' justify='space-between'>
            <Grid item xs={11}>
              <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
                Reactivate Bidders Targeting This Campaign
              </Typography>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <CloseIcon
                style={{ cursor: 'pointer', color: '#707070' }}
                onClick={this.handleCloseReactivateBidderDialog}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer', marginBottom: 10, marginTop: 10 }}
            onClick={this.handleReactivateBidders}
          >
            <SaveIcon style={{ color: '#5D66FA', marginLeft: 22 }} />
            <Typography style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 10 }}>
              Reactivate Selected Bidders
            </Typography>
          </Grid>
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer', marginBottom: 10, marginTop: 10 }}
            onClick={this.handleCloseReactivateBidderDialog}
          >
            <SaveIcon style={{ color: '#5D66FA', marginLeft: 22 }} />
            <Typography style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 10 }}>
              No Changes And Save Line Item
            </Typography>
          </Grid>
        </Grid>
      );
    };

    handleCloseReactivateBidderDialog = () => {
      this.setState(
        {
          openReactivateBiddersModal: false,
          // we want to ignore this modal popup if we want to save
          ignoreReactivateBidders: true,
          updating: false,
        },
        () => this.updateCampaign(this.state.campaignUpdating, false),
      );
    };

    handleReactivateBidders = () => {
      const selectedBidderIDs = [] as string[];
      for (let i = 0; i < this.state.inactiveBiddersTargetingCampaign.length; i++) {
        const bidder = this.state.inactiveBiddersTargetingCampaign[i];
        if (bidder.tableData.checked) {
          selectedBidderIDs.push(bidder.ID);
        }
      }

      // 'reactivate' key triggers slack notification
      bulkUpdateBidders(
        selectedBidderIDs.join(','),
        this.props.session.publisher.ID,
        { Active: HeaderBidderActiveActive },
        'Active,reactivate',
      ).then(
        () => {
          this.setState(
            {
              openReactivateBiddersModal: false,
              ignoreReactivateBidders: true,
              snackbarMessage: `Successfully updated bidders ${selectedBidderIDs.join(',')}`,
              updating: false,
            },
            () => this.updateCampaign(this.state.campaignUpdating, false),
          );
        },
        () => {
          this.setState(
            {
              openReactivateBiddersModal: false,
              ignoreReactivateBidders: true,
              snackbarMessage: `Failed to update bidders`,
            },
            () => this.updateCampaign(this.state.campaignUpdating, false),
          );
        },
      );
    };

    handleSelectionChangeCreativesModal = (rows: IBidderWithTableData[]) => {
      const inactiveBiddersTargetingCampaign = cloneDeep(
        this.state.inactiveBiddersTargetingCampaign,
      );
      for (const row of rows) {
        const rowID = row.ID.toString();
        for (let i = 0; i < inactiveBiddersTargetingCampaign.length; i++) {
          if (rowID === inactiveBiddersTargetingCampaign[i].ID) {
            inactiveBiddersTargetingCampaign[i] = row;
          }
        }
      }

      this.setState({
        inactiveBiddersTargetingCampaign,
      });
    };

    render() {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read
      const {
        campaigns,
        loading,
        snackbarError,
        snackbarMessage,
        readAuthorization,
        writeAuthorization,
        updating,
      } = this.state;
      const { classes, user, session, location, hideTopbar } = this.props;
      const { pathname } = location;

      if (campaigns.length === 0 && loading) {
        return <LoadingView active={true} text='Loading line items' />;
      }

      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }
      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started

      // Use Net CPM as title for Premion (HB-2214)
      const cpmTitle =
        this.props.session.publisher.ID === '788' || hasRoleSuperUser(this.props.user.user)
          ? 'Net CPM'
          : 'CPM';

      const columns: IListTableColumn[] = [
        {
          field: 'Active',
          title: 'Active',
          renderType: 'activeswitch',
          searchable: false,
          maxWidth: '75px',
        },
        { field: 'Name', title: 'Name', renderType: 'namelink', minWidth: '260' },
        {
          field: 'Status',
          title: 'Status',
          render: (rowData: ICampaignRowData) => this.renderStatusChip(rowData),
          searchable: false,
          maxWidth: '260px',
        },
        {
          field: 'StartDate',
          title: 'Start Date',
          renderType: 'date',
          searchable: false,
          customSort: this.compareStartDates,
          maxWidth: '125px',
        },
        {
          field: 'EndDate',
          field2: 'StartDate',
          title: 'End Date',
          renderType: 'date',
          searchable: false,
          customSort: this.compareEndDates,
          maxWidth: '125px',
        },
        {
          field: 'Advertiser',
          title: 'Advertiser',
          renderType: 'normal',
          searchable: true,
          maxWidth: '100px',
        },
        {
          field: 'CPM',
          title: cpmTitle,
          renderType: 'bignumber',
          searchable: false,
          maxWidth: '60px',
        },
        {
          field: 'DeliveredImps',
          field2: 'Progress',
          title: 'Delivered Imps',
          renderType: 'bignumber',
          searchable: false,
          maxWidth: '100px',
        },
        {
          field: 'Goal',
          title: 'Goal',
          renderType: 'bignumber',
          searchable: false,
          maxWidth: '100px',
        },
        {
          field: 'Pacing',
          title: 'Pacing',
          renderType: 'percent',
          searchable: false,
          maxWidth: '100px',
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
        { field: 'ID', title: 'ID', renderType: 'normal', searchable: true, hidden: true },
      ];

      const filteredCampaigns = this.filterCampaigns(campaigns);

      // generate row data, the object keys here need to match the fields above
      const data: ICampaignRowData[] = filteredCampaigns.map((campaign: IAdserverCampaign) => {
        const notEnoughData = this.shouldEnableCampaignPacingTooltip(campaign);

        const name = checkUserInfoMasked(this.props.user.user)
          ? `demo_line_item_${campaign.ID}`
          : campaign.Name;

        const order = this.state.AdserverOrders.find(
          (order: IAdserverOrder) => order.ID === campaign.AdserverOrderID,
        );
        let advertiserWithID = 'Unknown';
        if (order && order.Advertiser) {
          advertiserWithID = order.Advertiser
            ? `[${order.AdvertiserID}] ${obfuscateCompanyName(
                order.Advertiser,
                this.props.user.user.Username,
                order.Advertiser.Name,
              )}`
            : '';
        }

        return {
          // For active field, use boolean since all lists will likely have an active field and the typings may vary
          ID: campaign.ID.toString(),
          Active: campaign.Active === CampaignActiveActive,
          Name: `[${campaign.ID}] ${name}`,
          Status: campaign.Status || ECampaignStatus.Unknown,
          Progress:
            (
              ((parseInt(campaign.CRONQuantityCount, 10) || 0) * 100) /
              (parseInt(campaign.Quantity, 10) || 1)
            ).toFixed(2) + '% Completed',
          StartDate: parseDate(campaign.StartDate),
          EndDate: parseDate(campaign.EndDate),
          CPM:
            convertECurrencyTypeToSymbol(campaign.CurrencyDisplayed) +
              parseFloat(campaign.CPM).toFixed(2) || 0,
          DeliveredImps: parseInt(campaign.CRONQuantityCount, 10) || 0,
          Goal: parseInt(campaign.Quantity, 10) || 0,
          Campaign: campaign,
          Pacing: this.calculatePacing(campaign),
          TooltipEnabled: notEnoughData,
          TooltipText: this.generateCampaignPacingTooltipText(),
          Advertiser: advertiserWithID,
        };
      });

      return (
        <Panels maxWidth vertical relative minWidthMain>
          {!hideTopbar && (
            <Panel
              largeHorizontalPadding
              verticalPadding
              style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
            >
              <ModelTopBar
                title={routeName(pathname)}
                session={session}
                user={user}
                publisher={session.publisher}
                classes={classes}
                currentPath={pathname}
                disableTitlePaddingLeft
              />
            </Panel>
          )}
          <Panel scroll largePadding>
            <ListTable
              title={''}
              resource='line-item'
              columns={columns}
              data={data}
              isLoading={loading}
              isUpdating={updating}
              handleChangeActive={this.handleChangeActive}
              getItemSettingRoute={this.getCampaignSettingsRoute}
              handleDeleteItem={this.handleDeleteCampaign}
              handleCloneItem={this.cloneCampaignSettings}
              handleSetUpdating={this.handleSetUpdating}
              renderCustomToolbar={this.renderCustomToolbar}
              readRoleName={'read_campaign'}
              writeRoleName={'write_campaign'}
            />
            {/* validate reactivate bidders  */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={this.state.openReactivateBiddersModal}
              onClose={this.handleCloseReactivateBidderDialog}
            >
              <ModalSelectTable
                title={REACTIVATE_BIDDERS}
                items={this.state.inactiveBiddersTargetingCampaign}
                renderCustomToolbar={this.renderReactivateBiddersDialog}
                enableSearchBarLeft={true}
                // customColumns={[creativeStartDateColumn, creativeEndDateColumn]}
                interactive={false}
                enableMultiSelect={true}
                handleSelectionChange={this.handleSelectionChangeCreativesModal}
              />
            </Dialog>

            {/* snackbar for upload progress */}
            <Snackbar
              open={true}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
              }}
            >
              <ModelSnackBarProgressBarContainer uploadManager={this.uploadManager} />
            </Snackbar>
            <SnackbarView
              autoHideDuration={36000}
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
